import { useMutation } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuthStore } from '../../store/auth.store';
import authApi from '../api/auth.api';

export const useAuth = () => {
  const store = useAuthStore();

  const handleAuthSuccess = (data: any, rememberMe = false) => {
    if (data && data.user && data.accessToken && data.refreshToken) {
      store.setAuth(data.user, data.accessToken, data.refreshToken, rememberMe);
      if (typeof window !== 'undefined') {
        // Checked: persists ~30 days (matches backend refresh token TTL). Unchecked: a
        // true session cookie (no max-age) so it's dropped when the browser closes.
        const maxAge = rememberMe ? `; max-age=${60 * 60 * 24 * 30}` : '';
        document.cookie = `bb_token=${data.accessToken}; path=/${maxAge}`;
      }
    } else if (data && data.mfaRequired && data.mfaToken) {
      store.setMfaRequired(data.user, data.mfaToken);
    }
  };

  const loginMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string; rememberMe?: boolean }) =>
      authApi.login(credentials),
    onSuccess: (resEnvelope, variables) => {
      const data = resEnvelope.success ? resEnvelope.data : (resEnvelope as any);
      handleAuthSuccess(data, variables.rememberMe);
    },
  });

  const verifyTotpMutation = useMutation({
    mutationFn: ({ mfaToken, code }: { mfaToken: string; code: string; rememberMe?: boolean }) =>
      authApi.verifyTotpLogin(mfaToken, code),
    onSuccess: (resEnvelope, variables) => {
      const data = resEnvelope.success ? resEnvelope.data : (resEnvelope as any);
      handleAuthSuccess(data, variables.rememberMe);
    },
  });

  const passkeyLoginMutation = useMutation({
    mutationFn: async ({ email, rememberMe }: { email?: string; rememberMe?: boolean }) => {
      // 1. Get options from backend
      const optionsRes = await authApi.getPasskeyLoginOptions(email);
      const options = optionsRes.data;

      // 2. Pass options to browser WebAuthn API
      const authenticationResponse = await startAuthentication({ optionsJSON: options });

      // 3. Send verification payload to backend
      const verifyRes = await authApi.verifyPasskeyLogin({
        expectedChallenge: options.challenge,
        authenticationResponse,
        rememberMe,
      });

      return verifyRes;
    },
    onSuccess: (resEnvelope, variables) => {
      const data = resEnvelope.success ? resEnvelope.data : (resEnvelope as any);
      handleAuthSuccess(data, variables.rememberMe);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const token = store.refreshToken;
      if (token) {
        try {
          await authApi.logout(token);
        } catch (e) {
          // Ignore and proceed to local cleanup
        }
      }
      store.clearAuth();
      if (typeof window !== 'undefined') {
        document.cookie = 'bb_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
      }
    },
  });

  return {
    user: store.user,
    accessToken: store.accessToken,
    isAuthenticated: store.isAuthenticated,
    mfaRequired: store.mfaRequired,
    mfaToken: store.mfaToken,
    login: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    verifyTotp: verifyTotpMutation.mutateAsync,
    isVerifyingTotp: verifyTotpMutation.isPending,
    passkeyLogin: passkeyLoginMutation.mutateAsync,
    isPasskeyLoggingIn: passkeyLoginMutation.isPending,
    logout: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
    clearMfa: store.clearMfa,
    hasPermission: store.hasPermission,
  };
};

export default useAuth;
